import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { QESAlert, QESButton, QESCheckbox, QESSelect, QESTextInput } from "@app/shared/qes-standards";
import { Dispatch, FC, JSX, SetStateAction, useContext, useEffect, useMemo, useRef, useState } from "react";
import { ApiExportColumn, ExportColumn } from "../interfaces/exportColumns";
import { ExportService } from "@app/exports";
import { FlyoutFooter } from "@app/shared/Flyouts";
import { ColumnConfigurationPreset } from "../interfaces/columnConfigurationPreset";
import { FlyoutContext } from "@app/shared/Flyouts/FlyoutContext";
import { toast } from "react-toastify";
import { ExportModels } from "../interfaces/exportModels";
import { ReportType } from "../interfaces/reportTypeEnum";
import { ReportPresetColumns } from "../interfaces/reportPreset";
import { RoleRestricted, Roles, UserContext } from "@app/user";
import { TooltipWrapper } from "../../../shared";

export enum PanelMode {
  Create,
  Edit,
  Clone,
  View
}

interface ColumnConfigurationPanelProps {
  allPrivatePresetNames: string[];
  columnsEndpoint?: (model: ExportModels) => Promise<ExportColumn[]>;
  exportModel?: ExportModels;
  onSuccessfulSubmit?: () => void;
  panelMode: PanelMode;
  presetId?: number;
  reportType?: ReportType;
  setHasConfigurationChanges: Dispatch<SetStateAction<boolean>>;
}

export const ColumnConfigurationPanel: FC<ColumnConfigurationPanelProps> = (props: ColumnConfigurationPanelProps) => {
  const flyoutContext = useContext(FlyoutContext);
  const user = useContext(UserContext);
  const exportService = useRef(new ExportService()).current;
  const [presetName, setPresetName] = useState("");
  const [presetDescription, setPresetDescription] = useState("");
  const [isPrivate, setIsPrivate] = useState(true);
  const [presetNotFound, setPresetNotFound] = useState(false);
  const [columnsNotFound, setColumnsNotFound] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const draggedItem = useRef<HTMLDivElement>(null);
  const reportType = props.reportType;
  const presetId = props.presetId;
  const maxInputLength = 255;
  const isViewMode = props.panelMode === PanelMode.View;
  const isCloneMode = props.panelMode === PanelMode.Clone;
  const isEditMode = props.panelMode === PanelMode.Edit;
  const isCreateMode = props.panelMode === PanelMode.Create;
  const initialPresetName = useRef("");

  const [columns, setColumns] = useState<ReportPresetColumns[]>([]);
  const buttonClassnames = "font-weight-normal p-0 text-decoration-underline";

  useEffect(() => {
    if (!!presetId) {
      exportService.getColumnConfigurationPresetDetails(reportType, presetId).then((preset) => {
        const newPresetName = isCloneMode ? `${preset.name} Clone` : preset.name;
        setPresetName(newPresetName);
        initialPresetName.current = newPresetName;
        setPresetDescription(preset.description);
        setIsPrivate(preset.isPrivate);
        setColumns(sortColumnsByOrderAndVisibility(preset.columns));
      }).then(() => {
        setIsLoading(false);
      }).catch(() => {
        setPresetNotFound(true);
      });
    } else {
      getDefaultColumns().then((newColumns) => {
        setColumns(newColumns);
      }).then(() => {
        setIsLoading(false);
      });
    }
  }, [presetId, props.columnsEndpoint, props.exportModel, isViewMode, reportType]);

  const getDefaultColumns = async () => {
    try {
      const columnsResponse = await props.columnsEndpoint(props.exportModel);
      return sortColumnsByOrderAndVisibility(columnsResponse.map<ReportPresetColumns>((c) => {
        return {
          columnId: c.columnId,
          customName: c.name,
          customOrder: c.order,
          isHidden: false,
          isRequired: c.isRequired,
          isRestricted: c.isRestricted,
          name: c.name,
          order: c.order,
        };
      }));
    } catch (error) {
      setColumnsNotFound(true);
      setIsLoading(false);
    }
  }

  const sortColumnsByOrderAndVisibility = (newColumns: ReportPresetColumns[]) => {
    const hiddenCols: ReportPresetColumns[] = [];
    const visibleCols: ReportPresetColumns[] = [];
    newColumns.forEach((col) => {
      if (col.isHidden)
        hiddenCols.push(col);
      else
        visibleCols.push(col);
    });

    hiddenCols.sort((a, b) => a.customOrder > b.customOrder ? 1 : -1);
    visibleCols.sort((a, b) => a.customOrder > b.customOrder ? 1 : -1);

    const renderedCols = [...visibleCols, ...hiddenCols];
    renderedCols.forEach((rc, index) => {
      rc.customOrder = index + 1;
    });

    return renderedCols;
  }

  const sortOrderOptions = useMemo(() => {
    const options: JSX.Element[] = [];
    const hiddenCols = columns.filter((col) => col.isHidden);
    for (let i = 1; i <= columns.length; i += 1) {
      options.push(<option key={i} value={i} disabled={i > columns.length - hiddenCols.length}>{i}</option>);
    }
    return options;
  }, [columns]);

  const isPresetNameValid = useMemo(() => {
    const reservedPresetNames = new Set(props.allPrivatePresetNames);
    if (isEditMode) {
      reservedPresetNames.delete(initialPresetName.current);
    }
    return !!presetName && presetName.length <= maxInputLength && !reservedPresetNames.has(presetName);
  }, [presetName, props.allPrivatePresetNames]);

  const hasChanges = useMemo(() => {
    return !columns.every((col) => col.customName === col.name && col.customOrder === col.order && !col.isHidden);
  }, [columns]);

  useEffect(() => {
    props.setHasConfigurationChanges(hasChanges || !!presetName || !!presetDescription || !isPrivate);
  }, [hasChanges, presetName, presetDescription, isPrivate]);

  const handleRestore = () => {
    setIsLoading(true);

    getDefaultColumns().then((newColumns) => {
      setColumns(newColumns);
    }).then(() => {
      setIsLoading(false);
    });
  }

  // since this is onChange, it won't be triggered if oldIndex and newIndex are equal
  const handlePositionChange = (oldIndex: number, newIndex: number) => {
    const newColumns = structuredClone(columns);
    const updatedTempColumnOrder = newColumns
      .sort((a, b) => a.customOrder > b.customOrder ? 1 : -1)
      .filter((col) => !col.isHidden)
      .map((col) => col.columnId);

    const movingKey = updatedTempColumnOrder[oldIndex];
    const increment = newIndex < oldIndex ? -1 : 1;
    for (let k = oldIndex; k !== newIndex; k += increment) {
      updatedTempColumnOrder[k] = updatedTempColumnOrder[k + increment];
    }
    updatedTempColumnOrder[newIndex] = movingKey;
    updatedTempColumnOrder.forEach((key, index) => {
      newColumns.find((col) => col.columnId === key).customOrder = index + 1;
    })
    const sortedColumns = sortColumnsByOrderAndVisibility(newColumns);
    setColumns(sortedColumns);
  }

  const makeDraggable = (position: number) => {
    flyoutContext.flyoutBodyRef.current.querySelector(`.column-configuration__item[data-position="${position}"]`).setAttribute("draggable", "true");
  }

  const makeUndraggable = (position: number) => {
    flyoutContext.flyoutBodyRef.current.querySelector(`.column-configuration__item[data-position="${position}"]`).setAttribute("draggable", "false");
  }

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    const target = (e.target as HTMLElement).closest(".column-configuration__item");
    draggedItem.current = target as HTMLDivElement;
    const elDropPos = [...draggedItem.current.querySelectorAll(`[data-drop-position]`)];
    const prevSiblingDropPos = !!draggedItem.current.previousElementSibling
      ? [...draggedItem.current.previousElementSibling.querySelectorAll("[data-drop-position]")]
      : [];

    // if the previous sibling has more than one, we only want to exclude the second
    // if the current draggedItem is the first item in the group, its previous sibling doesn't matter and will be []
    const excludedEls = elDropPos.concat(prevSiblingDropPos[1] ? [prevSiblingDropPos[1]] : prevSiblingDropPos);
    (flyoutContext.flyoutBodyRef.current).querySelectorAll(".column-configuration__item:not(:has(.col-hidden)) .column-configuration__drop-zone")
      .forEach((el) => {
        if (
          !excludedEls.includes(el)
        ) {
          el.classList.add("qes-js-open");
        }
      });
  }

  const handleDragEnd = () => {
    draggedItem.current = null;
    flyoutContext.flyoutBodyRef.current.querySelectorAll(".column-configuration__drop-zone").forEach((el) => {
      if (el.classList.contains("qes-js-open")) {
        el.classList.remove("qes-js-open");
      }
      if (el.classList.contains("qes-js-drag-over")) {
        el.classList.remove("qes-js-drag-over");
      }
    });
  }

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const targetZone = e.target as HTMLElement;
    if (targetZone.classList.contains("qes-js-open")) {
      targetZone.classList.add("qes-js-drag-over");
    }
  }

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    (e.target as HTMLElement).classList.remove("qes-js-drag-over");
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, itemIndex: number, isFirstZoneInGroup: boolean) => {
    if (!(e.target as HTMLElement).classList.contains("qes-js-open")) {
      return;
    }
    e.stopPropagation();
    flyoutContext.flyoutBodyRef.current.querySelectorAll(".column-configuration__drop-zone").forEach((el) => {
      if (el.classList.contains("qes-js-open")) {
        el.classList.remove("qes-js-open");
      }
      if (el.classList.contains("qes-js-drag-over")) {
        el.classList.remove("qes-js-drag-over");
      }
    });

    const startItemPos = parseInt(draggedItem.current.getAttribute("data-position"));
    handlePositionChange(startItemPos, itemIndex + (startItemPos <= (itemIndex) ? 0 : isFirstZoneInGroup ? 0 : 1));
    draggedItem.current = null;
  }

  const handleNameChange = (columnId: string, newName: string) => {
    const newColumns = structuredClone(columns);
    newColumns.find((col) => col.columnId === columnId).customName = newName;
    setColumns(newColumns);
  }

  // known potential issue - the focus stays with the clicked button,
  // and if the button moves up above the viewport, the flyout will scroll back to the top
  const handleHideOrShow = (columnId: string, newIsHidden: boolean) => {
    const newColumns = structuredClone(columns);
    const thisCol = newColumns.find((col) => col.columnId === columnId);
    thisCol.isHidden = newIsHidden;

    // if the column is no longer hidden, restore its default sort order
    if (!newIsHidden) {
      thisCol.customOrder = thisCol.customOrder;
    }

    const sortedColumns = sortColumnsByOrderAndVisibility(newColumns);

    setColumns(sortedColumns);
  }

  const getExportModel = (): ColumnConfigurationPreset => {
    return {
      columns: columns.map<ApiExportColumn>((c) => ({
        columnId: c.columnId,
        customName: c.customName,
        customOrder: c.customOrder,
        isHidden: c.isHidden,
        isRestricted: c.isRestricted,
        name: c.name,
      })),
      description: presetDescription,
      exportType: reportType,
      isPrivate: isPrivate || !user.roles.includes(Roles.REPORT_PRESET_ADMIN),
      name: presetName,
    };
  }

  const handleCreate = () => {
    const exportModel = getExportModel();

    exportService.saveNewColumnConfigurationPreset(exportModel).then(() => {
      toast(`Preset ${presetName} has successfully been saved.`, {
        containerId: "z-index-9999",
        type: "success",
      });
      props.setHasConfigurationChanges(false);
      props.onSuccessfulSubmit();
      // bypass the are you sure modal
      flyoutContext.setIsFlyoutVisible(false);
    }, () => {
      toast("We encountered an error saving your column configuration preset.", {
        containerId: "z-index-9999",
        type: "error",
      });
    });
    setIsLoading(false);
  }

  const handleEdit = () => {
    const exportModel = getExportModel();

    exportService.editColumnConfigurationPreset(exportModel, props.presetId).then(() => {
      toast(`You have successfully saved the preset "${presetName}.`, {
        containerId: "z-index-9999",
        type: "success",
      });
      props.setHasConfigurationChanges(false);
      props.onSuccessfulSubmit();
      // bypass the are you sure modal
      flyoutContext.setIsFlyoutVisible(false);
    }, () => {
      toast("Failed to Save Preset. Please Try Again.", {
        containerId: "z-index-9999",
        type: "error",
      });
      setIsLoading(false);
    });
  }

  const handleClone = () => {
    const exportModel = getExportModel();

    exportService.saveNewColumnConfigurationPreset(exportModel).then(() => {
      toast(`You have successfully Cloned the preset "${presetName}.`, {
        containerId: "z-index-9999",
        type: "success",
      });
      props.setHasConfigurationChanges(false);
      props.onSuccessfulSubmit();
      // bypass the are you sure modal
      flyoutContext.setIsFlyoutVisible(false);
    }, () => {
      toast("Failed to Clone Preset. Please Try Again.", {
        containerId: "z-index-9999",
        type: "error",
      });
      setIsLoading(false);
    });
  }


  const saveActions = new Map<PanelMode, () => void>([
    [PanelMode.Create, handleCreate],
    [PanelMode.Edit, handleEdit],
    [PanelMode.Clone, handleClone],
  ])

  return (
    <div className="column-configuration-panel">
      <div className="position-relative">
        {/* Hiding these elements until we have the intended slideout/pop-out/pop-in/slide-in/whatever instead of a flyout */}
        {/* <QesIcon iconName={QesIcons.FontAwesome.ChevronRight} classNames="column-configuration-panel__close" />
        <h2 className="header-s">Column Configuration</h2> */}
        {presetNotFound && <QESAlert type="danger" persist={true}>This preset was not found in our system.</QESAlert>}
        {columnsNotFound && <QESAlert type="danger" persist={true}>We encountered a problem retrieving the columns for this report type.</QESAlert>}
        <p>
          Columns can be reordered, removed, and restored based on your needs.
          An asterisk (*) next to a column indicates that its values may not be
          displayed or any edits made may not be reflected in the final report
          due to licensing restrictions, user permissions, or project settings.
        </p>

        <QESTextInput
          label="Preset Name"
          required={true}
          value={presetName}
          onChange={(e) => setPresetName(e.target.value)}
          testId="column-configuration__preset-name"
          maxLength={maxInputLength}
          isValid={isPresetNameValid}
          disabled={isViewMode}
        />

        <QESTextInput
          label="Preset Description"
          maxLength={maxInputLength}
          value={presetDescription}
          onChange={(e) => setPresetDescription(e.target.value)}
          testId="column-configuration__preset-description"
          disabled={isViewMode}
        />

        <RoleRestricted allowedRoles={[Roles.REPORT_PRESET_ADMIN]}>
          <QESCheckbox
            label="This is a Private Preset (can be administered by you only)"
            checked={isPrivate}
            onChange={(e) => setIsPrivate(e.target.checked)}
            testId="column-configuration__private-preset"
            disabled={isViewMode}
          />
        </RoleRestricted>
      </div>

      <hr />

      {isCreateMode && (
        <div className="d-flex justify-content-end">
          <QESButton
            qesType="secondary"
            qesTestId="column-configuration__restore"
            className={buttonClassnames}
            onClick={handleRestore}
            disabled={!hasChanges}
          >
            Restore
          </QESButton>
        </div>
      )}

      {columns.map((col, columnIndex) => {
        return (
          <div
            key={col.columnId}
            className="column-configuration__item"
            draggable={false}
            data-position={columnIndex}
            onDragStart={(e) => handleDragStart(e)}
            onDragEnd={() => handleDragEnd()}
          >
            {columnIndex === 0 && !isViewMode
              && (
                <div
                  className={"column-configuration__drop-zone"}
                  onDragEnter={(e) => handleDragEnter(e)}
                  onDragOver={(e) => handleDragEnter(e)}
                  onDragLeave={(e) => handleDragLeave(e)}
                  onDrop={(e) => handleDrop(e, 0, true)}
                  data-drop-position={0}
                >
                  <div className="column-configuration__drop-indicator" />
                </div>
              )}
            <div className={`column-configuration__column-row${col.isHidden ? " col-hidden" : ""}`}>
              {/* drag n drop handle */}
              {!isViewMode && (
                <QesIcon
                  classNames="col-auto column-configuration__drag-handle"
                  iconName={QesIcons.FontAwesome.DragAndDropTexture}
                  description="click and drag to reorder columns"
                  svgAttributes={{
                    "aria-hidden": col.isHidden,
                    "onMouseOut": () => makeUndraggable(columnIndex),
                    "onMouseOver": () => makeDraggable(columnIndex),
                  }}
                />
              )}

              {/* reordering dropdown */}
              <QESSelect
                label="sort order"
                hideLabel={true}
                value={col.customOrder}
                onChange={(e) => handlePositionChange(col.customOrder - 1, parseInt(e.target.value) - 1)}
                testId={`column-configuration__sort-dropdown--${col.customOrder}`}
                className="column-configuration__sort-dropdown col-auto"
                disabled={col.isHidden || isViewMode}
              >
                {sortOrderOptions}
              </QESSelect>

              {/* text input (with descriptive text) */}
              <QESTextInput
                label="Column Name"
                hideLabel={true}
                descriptiveText={`${col.name}${col.isRestricted ? "*" : ""}`}
                descriptiveTextClassname={col.isRestricted ? "column-configuration__restricted-col-name" : ""}
                value={col.customName}
                onChange={(e) => handleNameChange(col.columnId, e.target.value)}
                testId={`column-configuration__name--${col.customOrder}`}
                containerClassName="flex-grow-1 ml-3"
                className="column-configuration__name"
                disabled={col.isHidden || isViewMode}
              />
              {/* "remove" x or undo button */}
              {!isViewMode && (
                col.isRequired
                  ? (
                    <TooltipWrapper text="This is a required field and cannot be removed." position="left" tooltipWidth="120px">
                      <QesIcon iconName={QesIcons.FontAwesome.InfoCircle} description="focus to reveal a tooltip" width="9" />
                    </TooltipWrapper>
                  )
                  :
                  col.isHidden
                    ? (
                      <QESButton
                        qesType="secondary"
                        className={`${buttonClassnames} col-auto`}
                        qesTestId={`column-configuration__undo--${col.customOrder}`}
                        onClick={() => handleHideOrShow(col.columnId, false)}
                        disabled={isViewMode}
                      >
                        Undo
                      </QESButton>
                    )
                    : (
                      <QESButton
                        qesType="secondary-danger"
                        className={`${buttonClassnames} col-auto column-configuration__delete`}
                        qesTestId={`column-configuration__undo--${col.customOrder}`}
                        onClick={() => handleHideOrShow(col.columnId, true)}
                        disabled={isViewMode}
                      >
                        <QesIcon iconName={QesIcons.FontAwesome.TimesLight} pathStyle={{ "stroke": "currentcolor" }} width="9" />
                      </QESButton>
                    )
              )}
            </div>
            {!isViewMode && (
              <div
                className="column-configuration__drop-zone"
                onDragEnter={(e) => handleDragEnter(e)}
                onDragOver={(e) => handleDragEnter(e)}
                onDragLeave={(e) => handleDragLeave(e)}
                onDrop={(e) => handleDrop(e, columnIndex, false)}
                data-drop-position={columnIndex}
              >
                <div className="column-configuration__drop-indicator" />
              </div>
            )}
          </div>
        );
      })}

      <FlyoutFooter
        submitButton={
          !isViewMode && (
            <QESButton
              qesType="primary"
              qesTestId="column-configuration__save"
              disabled={!isPresetNameValid || isLoading}
              onClick={() => { setIsLoading(true); saveActions.get(props.panelMode)(); }}
            >
              {isLoading ? (
                <div className="spinner-border spinner-border-sm" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              ) : "Save"}
            </QESButton>
          )
        }
      />
    </div>
  );
}